<template>
    <div
        class="q-pb-xl"
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="update" ref="profileForm">
            <q-input
                v-model="form.username"
                :label="$t('Username')"
                :disable="true"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                outlined
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-user" />
                </template>
            </q-input>

            <q-input
                v-model="form.email"
                :label="$t('Email')"
                @clear="form.email = ''"
                :rules="[val => validateEmail(val)]"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-envelope" />
                </template>
            </q-input>

            <q-input
                v-model="form.first_name"
                @clear="form.first_name = ''"
                :label="$t('First name')"
                :rules="[val => val !== '' || $t('Enter your first name')]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                for="first-name-input"
                bg-color="white"
                color="accent"
            />

            <q-input
                v-model="form.last_name"
                @clear="form.last_name = ''"
                :label="$t('Last name')"
                :rules="[val => val !== '' || $t('Enter your last name')]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                for="last-name-input"
                bg-color="white"
                color="accent"
            />

            <q-field
                v-model="form.phone_number"
                class="q-mb-sm"
                style="width: 100%"
                :label="$t('Phone number')"
                borderless
                stack-label
                outlined
                :rules="[val => isPhoneValid || $t('Invalid number')]"
                bg-color="white"
                tag="div"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-phone" />
                </template>
                <template v-slot:control>
                    <vue-tel-input
                        :wrapper-classes="[
                            'no-border',
                            'no-box-shadow',
                            'no-ouline'
                        ]"
                        class="input-vue-tel-input-in-qfield full-width"
                        style="height: 30px"
                        mode="international"
                        v-model="form.phone_number"
                        outlined
                        autoFormat
                        @validate="handlePhoneNumberInput"
                        validCharactersOnly
                    />
                </template>
            </q-field>

            <q-input
                v-model="form.job_title"
                @clear="form.job_title = ''"
                :label="$t('Job title')"
                class="q-mb-lg"
                style="width: 100%"
                clearable
                outlined
                for="job-title-input"
                bg-color="white"
                color="accent"
            />

            <q-input
                v-model="form.report_default_start_time"
                :label="$t('Report default start time')"
                class="q-mb-lg"
                style="width: 100%"
                outlined
                mask="##:##:##"
                @click="timePickerShow = true"
                bg-color="white"
                color="accent"
            >
                <template v-slot:append>
                    <q-icon
                        @click="
                            () => {
                                timePickerShow = true;
                            }
                        "
                        name="access_time"
                        class="cursor-pointer"
                    ></q-icon>
                </template>
            </q-input>

            <q-dialog v-model="timePickerShow">
                <q-card>
                    <time-picker
                        v-model="time"
                        :with-seconds="true"
                    ></time-picker>
                    <div class="row q-ma-md">
                        <div align="right" class="col">
                            <q-btn
                                @click="timePickerShow = false"
                                :label="$t('Cancel')"
                                class="q-mr-sm"
                                color="white"
                                text-color="primary"
                                outline
                            />
                            <q-btn
                                @click="handleTimePickerSelect"
                                :label="$t('Select')"
                                color="primary"
                            />
                        </div>
                    </div>
                </q-card>
            </q-dialog>

            <q-select
                v-model="form.default_time_picker"
                :label="$t('Default time picker')"
                :options="timePickerOptions"
                class="q-mb-lg"
                style="width: 100%"
                color="accent"
                emit-value
                map-options
                outlined
                bg-color="white"
            />
            <!-- role -->
            <q-input
                :model-value="getRoleLabel(form.role)"
                :disable="true"
                :label="$t('Role')"
                class="q-mb-lg"
                style="width: 100%"
                outlined
                bg-color="white"
                color="accent"
            >
            </q-input>
            <!-- language -->
            <q-select
                id="language-select"
                v-model="form.language"
                :label="$t('Language')"
                :options="languages"
                class="q-mb-lg"
                style="width: 100%"
                emit-value
                map-options
                outlined
                bg-color="white"
            />
            <!-- mailing lists -->
            <q-card class="mailing-lists" bordered flat>
                <q-card-section class="mailing-lists-section">
                    <q-toolbar class="text-secondary">
                        <q-toolbar-title>
                            {{ $t('Subscribe to mailing lists') }}
                        </q-toolbar-title>
                    </q-toolbar>
                </q-card-section>
                <q-separator></q-separator>
                <q-checkbox
                    v-model="form.subscribe_to_info_list"
                    :label="
                        $t(
                            'General information (new releases, announcements,...)'
                        )
                    "
                    color="accent"
                    style="width: 100%"
                />
                <q-checkbox
                    v-model="form.subscribe_to_system_list"
                    :label="$t('Service updates and outages')"
                    style="width: 100%"
                    color="accent"
                />
            </q-card>
            <br />
            <!-- permission card -->
            <q-card
                class="permission-card"
                bordered
                flat
                v-if="!$isUserPlanner()"
            >
                <q-card-section class="permission-card-section">
                    <q-toolbar class="text-secondary">
                        <q-icon name="fa fa-lock" color="5c5d5d"></q-icon>
                        <q-toolbar-title>
                            {{ `${$t('Permissions and Notifications')}` }}
                        </q-toolbar-title>
                    </q-toolbar>
                </q-card-section>
                <q-separator></q-separator>
                <q-card-section class="permission-card-section">
                    <!-- No access -->
                    <div
                        v-if="!groupedAccesses || groupedAccesses.length === 0"
                        class="q-mt-md row wrapper justify-center text-secondary vertical-middle"
                    >
                        <p>
                            <q-icon
                                name="fal fa-exclamation-triangle"
                                class="notation__icon"
                            />
                            {{
                                $t(
                                    'The user does not have access to any asset.'
                                )
                            }}
                        </p>
                    </div>

                    <!-- access list -->
                    <q-virtual-scroll
                        v-else
                        style="height: 300px"
                        :items="groupedAccesses"
                        v-slot="{ item: facility, index }"
                    >
                        <q-item-label
                            class="row"
                            :key="facility.facility_id"
                            header
                            style="color: black"
                            ><strong>
                                {{ facility.facility_name }}
                            </strong>
                        </q-item-label>
                        <q-item class="row" dense>
                            <q-item-section class="col-4">
                                <q-item-label caption>{{
                                    $t('Asset name')
                                }}</q-item-label>
                            </q-item-section>
                            <q-item-section class="col-4">
                                <span class="row">
                                    <q-item-label caption>{{
                                        $t('Cause Entry Alert')
                                    }}</q-item-label>
                                    <q-icon
                                        v-if="!isMobile"
                                        name="fa-light fa-circle-question fa-sm"
                                        class="q-ml-xs"
                                        color="secondary"
                                    >
                                        <q-tooltip
                                            anchor="top middle"
                                            self="bottom middle"
                                        >
                                            {{
                                                $t(
                                                    'If enabled, the user will be prompted to select a downtime cause when this asset goes into downtime.'
                                                )
                                            }}
                                        </q-tooltip>
                                    </q-icon>
                                </span>
                            </q-item-section>
                            <q-item-section class="col-4">
                                <span class="row">
                                    <q-item-label caption>{{
                                        $t('Event Alert')
                                    }}</q-item-label>
                                    <q-icon
                                        v-if="!isMobile"
                                        name="fa-light fa-circle-question fa-sm"
                                        class="q-ml-xs"
                                        color="secondary"
                                    >
                                        <q-tooltip
                                            anchor="top middle"
                                            self="bottom middle"
                                        >
                                            {{
                                                $t(
                                                    'Receive a notification when a subscribed downtime cause is logged on this asset.'
                                                )
                                            }}
                                        </q-tooltip>
                                    </q-icon>
                                </span>
                            </q-item-section>
                        </q-item>
                        <q-item
                            v-for="access in facility.accesses"
                            :key="access.asset_name"
                            dense
                        >
                            <asset-access
                                :asset-name="access.asset_name"
                                :asset-id="access.asset_id"
                                :asset-access-id="access.access_id"
                                :is-notified="access.is_notified"
                                :cause-subscription-count="
                                    access.subscribedCausesCount
                                "
                                @update:isNotified="access.is_notified = $event"
                                @update:causeSubscriptionCount="
                                    access.subscribedCausesCount = $event
                                "
                            />
                        </q-item>
                        <q-separator
                            inset
                            spaced
                            v-show="index !== groupedAccesses.length - 1"
                        ></q-separator>
                    </q-virtual-scroll>
                </q-card-section>
            </q-card>

            <q-card class="q-mt-md" bordered flat id="mfa">
                <q-card-section class="security-card-section">
                    <q-toolbar class="text-secondary">
                        <q-icon name="fa-solid fa-shield" color="5c5d5d" />
                        <q-toolbar-title>
                            {{ $t('Security') }}
                        </q-toolbar-title>
                    </q-toolbar>
                </q-card-section>

                <q-separator />
                <q-card-section class="security-card-section q-mt-sm">
                    <q-toolbar class="text-secondary">
                        <q-toolbar-title class="security-section-title">
                            {{ $t('Password') }}
                        </q-toolbar-title>
                        <q-btn
                            color="primary"
                            outline
                            @click="navigateToPasswordReset"
                            id="change-password-button"
                        >
                            {{ $t('Change Password') }}
                        </q-btn>
                    </q-toolbar>
                </q-card-section>

                <q-card-section class="security-card-section">
                    <q-toolbar class="text-secondary">
                        <q-toolbar-title class="security-section-title">
                            {{ $t('Multi-factor authentication') }}
                        </q-toolbar-title>
                    </q-toolbar>
                </q-card-section>

                <MFAPanel
                    :active-method="activeMFAMethod"
                    @refreshMethods="getMFAStatus"
                />
            </q-card>

            <div class="q-mt-xl">
                <q-btn
                    :label="$t('Save')"
                    class="float-right"
                    type="submit"
                    color="primary"
                    id="save-button"
                />
                <q-btn
                    @click="cancel"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm"
                    text-color="primary"
                    color="white"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { ROLES } from '@models/roles.js';
import { TIME_PICKER_OPTIONS } from '@models/timepickeroptions.js';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import TimePicker from '@components/TimePicker.vue';
import MFAPanel from '@components/MFAPanel.vue';
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useLanguagesStore } from '@stores/languages.js';
import AssetAccess from '@components/AssetAccess.vue';

export default {
    name: 'Profile',
    mixins: [BreadcrumbsManager],
    components: {
        VueTelInput,
        TimePicker,
        MFAPanel,
        AssetAccess
    },
    async created() {
        this.groupedAccesses = this.groupAccessesByFacility(
            this.user['accesses']
        );

        // initialize form
        this.initForm();
        this.time = this.form.report_default_start_time;

        // get MFA status
        await this.getMFAStatus();
        await this.loadAssetAccesses();
    },
    mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Home'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Profile')
            }
        ]);
    },
    data() {
        return {
            TIME_PICKER_OPTIONS: TIME_PICKER_OPTIONS,
            form: {
                username: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                job_title: '',
                report_default_start_time: '00:00:00',
                default_time_picker: 'digital',
                role: '',
                language: '',
                subscribe_to_info_list: true,
                subscribe_to_system_list: true
            },
            isPhoneValid: true,
            groupedAccesses: null,
            timePickerShow: false,
            time: '00:00:00',

            // MFA
            activeMFAMethod: null
        };
    },
    computed: {
        ...mapState(useAuthStore, ['user']),
        ...mapState(useLanguagesStore, ['getLanguages']),
        /** User ID */
        id() {
            return this.user['id'];
        },
        /**
         * Language options that show label/keys.
         */
        languages() {
            return this.getLanguages.map(lang => {
                return {
                    label: `${lang['label']} / ${lang['value']}`,
                    value: lang['value']
                };
            });
        },
        /**
         * Time Picker Options.
         */
        timePickerOptions() {
            return this.TIME_PICKER_OPTIONS.map(timePicker => {
                return {
                    label: this.$t(timePicker['label']),
                    value: timePicker['value']
                };
            });
        },
        /**
         * Check if the platform is mobile.
         * @return {Boolean} - True if the platform is mobile.
         */
        isMobile() {
            return this.$q.screen.lt.sm;
        }
    },
    methods: {
        ...mapActions(useAuthStore, [
            'updateCurrentUserAction',
            'logoutAction',
            'loadSubscribedCausesAction'
        ]),
        /**
         * Get role name from value.
         * @param {String} role - Role value.
         * @return {String} - Role label.
         */
        getRoleLabel(role_value) {
            return ROLES[role_value] || this.$t('Unknown');
        },
        /**
         * Initialize form.
         */
        initForm() {
            this.form = Object.assign(this.form, this.user);
            this.form['phone_number'] = this.user.phone_number
                ? this.user.phone_number
                : '';
        },
        /**
         * Update user.
         */
        async update() {
            // prepare the payload with server accepted format
            const payload = {
                id: this.user['id'],
                username: this.form.username,
                first_name: this.form.first_name,
                last_name: this.form.last_name,
                email: this.form.email,
                job_title: this.form.job_title,
                report_default_start_time: this.form.report_default_start_time,
                default_time_picker: this.form.default_time_picker,
                role: this.form.role,
                language: this.form.language,
                accesses: this.ungroupAccesses(this.groupedAccesses),
                subscribe_to_info_list: this.form.subscribe_to_info_list,
                subscribe_to_system_list: this.form.subscribe_to_system_list
            };
            if (this.form.phone_number.replace(/ /g, '') !== '') {
                payload['phone_number'] = this.form.phone_number.replace(
                    / /g,
                    ''
                );
            } else {
                payload['phone_number'] = null;
            }
            // check if the following fields are changed before updating the user object in VueX store
            // this is required to determine if more actions should be done after updating these fields
            const isLanguageChanged =
                this.form['language'] !== this.user['language'];
            const isEmailChanged = this.form['email'] !== this.user['email'];
            // dispatch action
            try {
                await this.updateCurrentUserAction(payload);
                this.$notifySuccess(this.$t('Profile updated'));
                // check if the email is changed
                if (isEmailChanged) {
                    // notify the user the new link
                    this.$notifySuccess(
                        this.$t(
                            'A confirmation link has been sent to your new e-mail.'
                        )
                    );
                    // log out to avoid displaying "Account inactive" error
                    await this.logoutAction();
                }
                // update language used for the app
                if (isLanguageChanged) {
                    this.$i18n.locale = payload['language'];
                }
                // update the causes subscriptions in the store
                await this.loadSubscribedCausesAction();
                // route to facility list page (home page)
                this.$router.push({
                    name: 'facility_list',
                    params: {}
                });
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if (
                    error['email'] &&
                    error['email'].length > 0 &&
                    error['email'].includes(
                        'user with this email already exists.'
                    )
                ) {
                    const errorMessage = this.$t(
                        'An account with this email address already exists.'
                    );
                    this.$notifyError(errorMessage, error['email']);
                    return;
                } else if (
                    error['email'] &&
                    error['email'].length > 0 &&
                    error['email'].includes('allowed_email_domains')
                ) {
                    const allowed_email_domains =
                        error['allowed_email_domains'];

                    if (allowed_email_domains.length > 0) {
                        this.$notifyError(
                            this.$t(
                                'Email domain is not allowed for this client. Only the following domains are allowed: {allowed_email_domains}.',
                                {
                                    allowed_email_domains:
                                        allowed_email_domains.join(', ')
                                }
                            ),
                            error['email']
                        );
                    } else {
                        // If no allowed email domains are provided, fall back to generic error message
                        this.$notifyError(
                            this.$t(
                                'Email domain is not allowed for this client.'
                            ),
                            error['email']
                        );
                    }
                }

                // Fall back to generic error message
                this.$notifyError(
                    this.$t(
                        'Failed to update profile. Please try again later.'
                    ),
                    error
                );
            }
        },
        /**
         * Cancel the profile.
         */
        async cancel() {
            // route to facility list page
            // update the causes subscriptions in the store
            await this.loadSubscribedCausesAction();
            this.$router.push({
                name: 'facility_list',
                params: {}
            });
        },
        /**
         * Group the list of accesses by facility id.
         * Returned data format:
         * [
         *    {
         *       "facility": 1, "facility_name": "Saskatoon",
         *       accesses: [
         *           {"access_id": 1, "asset_id": 1, "asset_name": "asset0", "is_notified": false},
         *           {"access_id": 2, "asset_id": 2, "asset_name": "asset1", "is_notified": true},
         *           {"access_id": 3, "asset_id": 5, "asset_name": "asset6", "is_notified": false},
         *       ]
         *    },
         *    { "facility": 2, "facility_name": "Georgetown",
         *       accesses: [
         *           {"access_id": 4, "asset_id": 7, "asset_name": "asset8", "is_notified": false},
         *           {"access_id": 5, "asset_id": 8, "asset_name": "asset9", "is_notified": true},
         *           {"access_id": 6, "asset_id": 9, "asset_name": "asset10", "is_notified": false}
         *       ]
         *    }
         * ]
         * @param {Object[]} inputAccesses - Raw access list
         * @return {Object[]} - access list that is grouped by facility id
         */
        groupAccessesByFacility(inputAccesses) {
            // define the result list
            let groupedAccesses = [];
            let accessMap = new Map();
            // create a sorted list by facility id and asset id before grouping them to ensure alphabetical order
            const sortedAccesses = [...inputAccesses].sort(
                (a, b) =>
                    a['facility_name'].localeCompare(b['facility_name']) ||
                    a['asset_name'].localeCompare(b['asset_name'])
            );
            // iterate the input access list and look every element up in the map
            sortedAccesses.forEach(access => {
                const facilityId = access['facility'];
                if (!accessMap.has(facilityId)) {
                    accessMap.set(facilityId, {
                        facility_name: access['facility_name'],
                        accesses: []
                    });
                }

                accessMap.get(facilityId)['accesses'].push({
                    access_id: access['id'],
                    asset_id: access['asset'],
                    asset_name: access['asset_name'],
                    is_notified: access['is_notified'],
                    causes: access['causes']
                });
            });

            for (let key of accessMap.keys()) {
                groupedAccesses.push({
                    facility_id: key,
                    facility_name: accessMap.get(key)['facility_name'],
                    accesses: accessMap.get(key)['accesses']
                });
            }
            return groupedAccesses;
        },
        /**
         * Ungroup the list and prepare the list for query
         * Returned data format:
         *  [
                { "facility": 1, "asset": 1, "is_notified": false },
                { "facility": 2, "asset": 4, "is_notified": false },
                { "facility": 3, "asset": 6, "is_notified": false },
            ]
            @param {Object[]} inputAccesses - input accesses list
            @return {Object[]} - the output list
         */
        ungroupAccesses(inputAccesses) {
            let outputAccesses = [];
            inputAccesses.forEach(facility => {
                facility['accesses'].forEach(access => {
                    outputAccesses.push({
                        facility: facility['facility_id'],
                        asset: access['asset_id'],
                        is_notified: access['is_notified'],
                        causes: access['causes']
                    });
                });
            });
            return outputAccesses;
        },
        /**
         * Validate the email input
         * @param {String} val - User input from email field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateEmail(val) {
            // false is an error
            if (val.length > 0) {
                // regular expression for email

                const regex =
                    /^\w+([.-]?\w+)*(\+\w+)?@\w+([.-]?\w+)*(\.\w{2,3})+$/;
                if (!regex.test(val)) {
                    return this.$t('Invalid email format');
                }
            } else {
                return this.$t('Email cannot be empty');
            }
            return true;
        },
        /**
         * Vue-tel functions
         */
        handlePhoneNumberInput(phoneObject) {
            if (phoneObject['valid'] === false) {
                this.isPhoneValid = phoneObject['valid'];
            } else {
                this.isPhoneValid = true;
            }
        },
        /**
         * Handle the time select
         */
        handleTimePickerSelect() {
            this.timePickerShow = false;
            this.form.report_default_start_time = this.time;
        },
        /**
         * Get MFA status and set the mfaEnabled flag.
         */
        async getMFAStatus() {
            try {
                const response = await this.$axios.get(
                    '/auth/mfa/user-active-methods/'
                );
                this.activeMFAMethod =
                    response.data['results'].find(method => method.is_primary)
                        ?.name || response.data[0]?.name;
            } catch (error) {
                this.$notifyError(this.$t('Failed to get MFA status.'));
                console.error(error);
            }
        },
        /**
         * Navigate to the password reset page.
         */
        navigateToPasswordReset() {
            this.$router.push({
                name: 'reset_password_from_profile'
            });
        },
        /**
         * Fetch all asset accesses for the user, handling pagination.
         * @return {Array} - List of all asset accesses for the user.
         */
        async fetchAllAssetAccesses() {
            let results = [];

            // Viewer and Planner roles don't have permission to request asset access from the backend,
            // so we skip asset access lookups for them and return an empty list.
            if (this.$isUserViewer() || this.$isUserPlanner()) {
                return results;
            }

            let nextUrl = `/assetaccesses/?user_id=${this.id}&summary=true`;
            while (nextUrl) {
                try {
                    const response = await this.$axios.get(nextUrl);
                    results = results.concat(response.data.results);
                    nextUrl = response.data.next;
                } catch (error) {
                    this.$notifyError(
                        this.$t('Failed to load asset accesses.')
                    );
                    console.error(error);
                    break;
                }
            }
            return results;
        },
        /**
         * Load asset accesses for the user
         * For now, this is read only endpoint
         * and we are just using to get the total subscription count
         * from for each asset access. In future, we will use this
         * endpoint to also manage asset access subscriptions.
         * @return {Promise<void>} - A promise that resolves when asset accesses are loaded.
         */
        async loadAssetAccesses() {
            try {
                const assetAccesses = await this.fetchAllAssetAccesses();
                this.groupedAccesses.forEach(facility => {
                    facility.accesses.forEach(access => {
                        const matchedAssetAccess = assetAccesses.find(
                            aa => aa.asset === access.asset_id
                        );
                        if (matchedAssetAccess) {
                            access.subscribedCausesCount =
                                matchedAssetAccess.subscribed_causes_count;
                        } else {
                            access.subscribedCausesCount = 0;
                        }
                    });
                });
            } catch (error) {
                this.$notifyError(this.$t('Failed to load asset accesses.'));
                console.error(error);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.permission-card-section,
.security-card-section {
    padding: 2px;
}

.security-section-title {
    font-size: 18px;
}

.mailing-lists-section {
    padding: 2px;
}

.notation__icon {
    font-size: 200%;
    margin-right: 8px;
}

/* note: the height 98% is to avoid the inner input to hide the bottom border of q-field */
.input-vue-tel-input-in-qfield {
    height: 98%;
    width: 98%;
    margin-bottom: auto;
    border-style: none;
}

/* note: I use color dark for my text, I am forcing it because otherwise it is the same color as q-field - so here primary color or negative color when error - */
.wrapper-vue-tel-input-in-qfield li.vti__dropdown-item {
    color: var(--q-color-dark);
}

/* The rounded property of the q-banner is not working, so this class forces the border-radius
*/
.warning-banner {
    border-radius: 5px !important;
}

.mfa-icon {
    margin-right: 6px;
    height: 1.3em;
}

.security-section-title {
    height: 20px;
}
</style>
